import axios from 'axios';
import { api, versao } from '../config';
import { getHeaders } from '../actions/localStorage';

// Usando nosso próprio backend como proxy para a API do ERP
// Isso evita problemas de CORS quando chamamos a API do ERP diretamente do navegador
const apiErpProxy = axios.create({
  baseURL: `${api}/${versao}/api/erp-proxy`
});

// Adiciona os headers de autenticação nas requisições
apiErpProxy.interceptors.request.use(request => {
  const headers = getHeaders();
  request.headers = { ...request.headers, ...headers.headers };
  return request;
});

function mensagemErroProxy(error) {
  const data = error.response?.data;
  if (data == null) return error.message;
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  return error.message;
}

/** Id + nome quando GET formas_pagamentos no ERP falha (403, rede, etc.) */
const FORMAS_PAGAMENTO_FALLBACK = [
  { id: '819968', nome: 'C6 Crédito link' },
  { id: '194867', nome: 'Cartão de Crédito' },
  { id: '194868', nome: 'Cartão de Débito' },
  { id: '194872', nome: 'Devolução de Mercadorias' },
  { id: '194866', nome: 'Dinheiro' },
  { id: '3069517', nome: 'Faturada pix' },
  { id: '3491845', nome: 'Pag Seguro' },
  { id: '1591223', nome: 'Paypal' },
  { id: '895597', nome: 'Pix C6' },
  { id: '3069516', nome: 'Pix site' },
  { id: '911208', nome: 'Site Crédito' },
  { id: '4237119', nome: 'STONE' }
];

function formasPagamentoParaSelect(lista) {
  return lista.map((item) => ({
    FormasPagamento: {
      id: String(item.id),
      nome: item.nome,
      descricao: item.nome
    }
  }));
}

export const erpApi = {
  // Produtos
  getProdutoByCodigo: async (codigo) => {
    try {
      const codigoFormatado = encodeURIComponent(codigo.trim());
      const response = await apiErpProxy.get(`/produtos/codigo/${codigoFormatado}`);
      
      console.log("Resposta original da API:", response.data);
      
      // Verifica se temos uma resposta válida
      if (response.data) {
        // Verificação para diferentes formatos de resposta possíveis
        let produtos = [];
        
        // Formato como na captura de tela - objeto com índices numéricos "0": {...}
        if (typeof response.data === 'object' && response.data["0"] && response.data["0"].id) {
          // Converte o objeto em um array de produtos
          produtos = Object.values(response.data);
          console.log("Detectado formato com índices numéricos:", produtos);
        } 
        // Estrutura original esperada - { code, status, meta, data }
        else if (response.data.code === 200 && response.data.status === "success" && Array.isArray(response.data.data)) {
          produtos = response.data.data;
          console.log("Detectado formato padrão com data array:", produtos);
        } 
        // Resposta já é um array diretamente
        else if (Array.isArray(response.data)) {
          produtos = response.data;
          console.log("Detectado formato array direto:", produtos);
        }
        // Resposta é um único objeto sem índices numéricos (não é um array)
        else if (typeof response.data === 'object' && response.data.id) {
          produtos = [response.data];
          console.log("Detectado formato objeto único:", produtos);
        }
        else {
          console.error("Formato de resposta não reconhecido:", response.data);
          return { 
            success: false, 
            error: { message: "Formato de resposta inválido da API ERP" } 
          };
        }
        
        // Verificação para produtos vazios
        if (produtos.length === 0) {
          return { 
            success: false, 
            error: { message: "Produto não encontrado no ERP" } 
          };
        }
        
        // Mapear os produtos para um formato mais amigável
        const produtosMapeados = produtos.map(produto => ({
          codigo: produto.codigo_interno || produto.id,
          nome: produto.nome,
          descricao: produto.descricao || "",
          preco: produto.valor_venda ? parseFloat(produto.valor_venda) : 0,
          quantidade: produto.estoque !== undefined ? parseInt(produto.estoque) : 0,
        }));
        
        console.log("Produtos mapeados:", produtosMapeados);
        return { success: true, data: produtosMapeados };
      } else {
        return { 
          success: false, 
          error: { message: "Formato de resposta inválido da API ERP" } 
        };
      }
    } catch (error) {
      console.error("Erro na requisição ao ERP:", error);
      return { 
        success: false, 
        error: { message: mensagemErroProxy(error) }
      };
    }
  },

  // Formas de Pagamento
  getFormasPagamento: async () => {
    const fallback = () => ({
      success: true,
      data: formasPagamentoParaSelect(FORMAS_PAGAMENTO_FALLBACK)
    });
    try {
      const response = await apiErpProxy.get('/formas_pagamentos');
      
      if (response.data) {
        // Verificação para diferentes formatos de resposta possíveis
        let formasPagamento = [];
        
        // Formato como índices numéricos "0": {...}
        if (typeof response.data === 'object' && response.data["0"]) {
          formasPagamento = Object.values(response.data);
        } 
        // Estrutura com { code, status, meta, data }
        else if (response.data.code === 200 && response.data.status === "success" && Array.isArray(response.data.data)) {
          formasPagamento = response.data.data;
        } 
        // Resposta já é um array diretamente
        else if (Array.isArray(response.data)) {
          formasPagamento = response.data;
        }
        else {
          console.error("Formas de pagamento: formato não reconhecido, usando fallback", response.data);
          return fallback();
        }
        
        if (formasPagamento.length === 0) {
          return fallback();
        }
        // Garante id/nome no shape esperado pelo select (FormasPagamento.*)
        const normalizadas = formasPagamento.map((row) => {
          const id = row.FormasPagamento?.id ?? row.id;
          const nome = row.FormasPagamento?.nome ?? row.nome;
          if (id == null) return null;
          return {
            FormasPagamento: {
              id: String(id),
              nome: nome || `Forma ${id}`,
              descricao: row.FormasPagamento?.descricao ?? row.descricao ?? nome
            }
          };
        }).filter(Boolean);
        if (normalizadas.length === 0) {
          return fallback();
        }
        return { success: true, data: normalizadas };
      } else {
        return fallback();
      }
    } catch (error) {
      console.error("Formas de pagamento: Erro no ERP, usando fallback", error);
      return fallback();
    }
  },

  // Funcionários (vendedores)
  getFuncionarios: async () => {
    try {
      const response = await apiErpProxy.get('/funcionarios');
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: { message: mensagemErroProxy(error) }
      };
    }
  },

  // Método genérico para fazer qualquer requisição ao ERP
  request: async (endpoint, method = 'GET', data = null, params = {}) => {
    try {
      const response = await apiErpProxy.post('/request', {
        endpoint,
        method,
        data,
        params
      });
      
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: mensagemErroProxy(error) }
      };
    }
  }
};

export default erpApi; 